import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Form } from 'antd';
import {
  Button,
  Checkbox,
  Divider,
  Input,
  InputNumber,
  Select,
  TextArea,
} from '@prio365/prio365-react-library';
import { useTranslation } from 'react-i18next';
import { makePrioStyles } from '../../../../theme/utils';

import {
  TimeRecord,
  TimeRecordFormModel,
  TimeRecordEntry,
  CreateTimeRecordsRequest,
  TimeRecordEntryMoment,
} from '../../../../models/TimeRecord';
import {
  ContactId,
  DateTimeString,
  EditTimeRecordContextType,
  ProjectId,
} from '../../../../models/Types';

import { useDispatch, useSelector } from 'react-redux';
import {
  fetchInternalProjectContacts,
  syncProjects,
} from '../../../projects/actions';
import {
  getActiveProjectWithoutMe,
  getUserMeContactId,
  getUserMeIsFetching,
  getInternalProjectContactsIsFetching,
} from '../../../../apps/main/rootReducer';
import { useAccessRights } from '../../../users/hooks/useAccessRights';
import { colon } from '../../../../util/forms';
import TimeRecordEntryManagement from './TimeRecordEntryManagement/TimeRecordEntryManagement';
import { v4 as uuid } from 'uuid';
import { sumUpDurationInMinutes } from '../../util';
import { Moment } from 'moment-timezone';
import * as MomentObject from 'moment-timezone';
import { extendMoment } from 'moment-range';
import classNames from 'classnames';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../../theme/types';
import Flex from '../../../../components/Flex';
import { ProjectPhase } from '../../../../models/ProjectPhase';
import { apiGetProjectPhases } from '../../../projects/api';
import { useProjectContacts } from '../../../projects/hooks/useProjectContacts';
import useProjectsContext from '../../../projects/hooks/useProjectsContext';
import ContactPickerPrioCL from '../../../contacts/components/ContactPickerPrioCL';
import ProjectPickerPrioCL from '../../../projects/components/ProjectPickerPrioCL';

const moment = extendMoment(MomentObject);

const useStyles = makePrioStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 1024,
    height: '100%',
    '&.ant-form': {
      marginTop: '8px',
    },
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  fullWidth: {
    width: '100%',
  },
  submitButtonFormItem: {
    textAlign: 'right',
    marginBottom: 0,
  },
  actionButtonsRow: {
    marginTop: 24,
  },
  datePicker: {
    width: '100%',
  },
  timePicker: {
    width: `calc(75% - ${16 + theme.spacing.small}px)`,
    marginRight: theme.spacing.small,
  },
  duration: {
    marginRight: theme.spacing.regular,
    transition: 'border-color 0.3s',
  },
  space: {
    display: 'flex',
  },
  periodSum: {
    textAlign: 'right',
  },
  mutedColor: {
    color: theme.colors.application.typography.muted,
  },
  deleteButton: {
    float: 'left',
    backgroundColor: 'transparent',
    padding: 0,
  },
  warning: {
    color: theme.colors.base.red.default,
  },
  scrollable: {
    height: 'calc(100% - 56px - 4px)',
    overflowX: 'hidden',
    overflowY: 'auto',
    flex: 1,
  },
  noMargin: {
    marginBottom: '0px',
  },
  divider: {
    margin: '8px 0px 24px 0px',
  },
  divider2: {
    margin: '0px 0px 24px 0px',
  },
}));

interface CreateTimeRecordFormProps {
  className?: string;
  disableActionButton?: boolean;
  disableForm?: boolean;
  actionLabel: string;
  cancelLabel?: string;
  onFinish: (
    value: CreateTimeRecordsRequest | TimeRecord,
    isMe: boolean
  ) => void;
  onCancel?: () => void;
  resetOnFinish?: boolean;
  starTime?: DateTimeString;
  endTime?: DateTimeString;
}

export const CreateTimeRecordForm: React.FC<CreateTimeRecordFormProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const {
    className,
    actionLabel,
    cancelLabel,
    onFinish,
    onCancel,
    disableActionButton,
    disableForm,
    resetOnFinish,
    starTime,
    endTime,
  } = props;

  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [timeRecordForm] = Form.useForm();

  const [projectPhases, setProjectPhases] = useState<ProjectPhase[]>([]);

  const resetTimerecordformRef = useRef<VoidFunction>(null);
  const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);
  const [descriptionIsRequired, setDescriptionIsRequired] = useState(false);

  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMeContactId = useSelector(getUserMeContactId);
  const activeProject = useSelector(getActiveProjectWithoutMe);
  const inintialTimeRecordEntries: TimeRecordEntryMoment[] = [
    {
      startTime: (starTime ? moment(starTime) : moment())
        .startOf('day')
        .add(8, 'hours'),
      endTime: (endTime ? moment(endTime) : moment())
        .startOf('day')
        .add(16, 'hours'),
    },
  ];
  const isFetchingMe = useSelector(getUserMeIsFetching);

  const [currentProjectId, setCurrentProject] =
    useState<ProjectId>(activeProject);

  const { myProjects, getProjectById } = useProjectsContext();

  const currentProject = useMemo(() => {
    return getProjectById(currentProjectId);
  }, [currentProjectId, getProjectById]);

  const { internalProjectContactContactIds: projectContactIds } =
    useProjectContacts(currentProjectId);

  const isMyProject = useMemo(() => {
    return myProjects.some(({ projectId }) => projectId === currentProjectId);
  }, [myProjects, currentProjectId]);
  const myProjectIds = useMemo(() => {
    return myProjects.map(({ projectId }) => projectId);
  }, [myProjects]);

  const isInternalFetching = useSelector(getInternalProjectContactsIsFetching);

  const accessRights = useAccessRights(
    [
      'writeOtherUserTimeRecord',
      'writeOtherUserTimeRecordByProjectRole',
      'writeOtherUserTimeRecordByGlobalRole',
    ],
    { projectId: currentProjectId, companyId: currentProject?.subsidiaryId }
  );
  const canSelectContact = accessRights['writeOtherUserTimeRecord'];

  const [contact, setContact] = useState<ContactId>(
    isMyProject ? (userMeContactId ?? undefined) : undefined
  );

  const contextType: EditTimeRecordContextType = useMemo(() => {
    if (accessRights['writeOtherUserTimeRecordByGlobalRole']) {
      return 'global';
    }
    return 'office';
  }, [accessRights]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const cancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleFinish: (value: TimeRecordFormModel) => Promise<void> = async (
    value: TimeRecordFormModel
  ) => {
    const timeRecords: TimeRecord[] = [];
    await timeRecordForm
      .validateFields()
      .then(async (timeRecordResult: [Moment, Moment][]) => {
        await form
          .validateFields()
          .then(async (result: TimeRecordFormModel) => {
            result.dayRecordEntries.forEach((dayRecord, index) => {
              const timeRecordEntriesStrings: TimeRecordEntry[] = [];
              if (result.dayRecordEntries.length > 1) {
                dayRecord.timeRecordEntries.forEach((timeRecord) => {
                  timeRecordEntriesStrings.push({
                    startTime: `${
                      dayRecord.date.toISOString(true).split('T')[0]
                    }T${timeRecord.startTime
                      .toISOString(true)
                      .split('T')[1]
                      .slice(0, 12)}Z`,

                    endTime: `${
                      dayRecord.date.toISOString(true).split('T')[0]
                    }T${timeRecord.endTime
                      .toISOString(true)
                      .split('T')[1]
                      .slice(0, 12)}Z`,
                  });
                });
              } else {
                timeRecordResult['periods'].forEach((timeRecord) => {
                  timeRecordEntriesStrings.push({
                    startTime: `${
                      dayRecord.date.toISOString(true).split('T')[0]
                    }T${timeRecord[0]
                      .toISOString(true)
                      .split('T')[1]
                      .slice(0, 12)}Z`,

                    endTime: `${
                      dayRecord.date.toISOString(true).split('T')[0]
                    }T${timeRecord[1]
                      .toISOString(true)
                      .split('T')[1]
                      .slice(0, 12)}Z`,
                  });
                });
              }

              const guid = uuid();

              const _kilometerDistance =
                value.dayRecordEntries.length > 1
                  ? (dayRecord?.kilometerDistance?.toString() ??
                    value.kilometerDistance)
                  : value.kilometerDistance;
              const kilometerDistance = !_kilometerDistance
                ? 0
                : parseInt(_kilometerDistance);

              timeRecords.push({
                timeRecordId: guid,
                title: value.title,
                projectId: value.projectId,
                day: dayRecord.date.toISOString(true).split('T')[0],
                kilometerDistance: kilometerDistance,
                durationInMinutes: sumUpDurationInMinutes(
                  timeRecordEntriesStrings
                ),
                contactId: value.contactId,
                description: value.description,
                timeRecordEntries: timeRecordEntriesStrings,
                projectPhaseId: value.projectPhaseId,
                isSeparateService: value.isSeparateService,
              });
            });
          });
      });
    const request: CreateTimeRecordsRequest = {
      contactId: value.contactId,
      projectId: value.projectId,
      timeRecords: timeRecords,
    };

    onFinish(request, request.contactId === userMeContactId);
    if (resetOnFinish) {
      const dayRange: [Moment, Moment] =
        timeRecordForm.getFieldsValue(true).dayRange;
      const startDate = dayRange[0];
      const endDate = dayRange[1];
      timeRecordForm.setFieldsValue({
        dayRange: [startDate, endDate],
        periods: [[moment('8:00', 'h:mm a'), moment('16:00', 'h:mm a')]],
      });
      resetTimerecordformRef.current();

      form.setFieldsValue({
        projectId: currentProjectId,
        contactId: value.contactId,
        title: '',
        kilometerDistance: undefined,
        description: '',
        isSeparateService: false,
        projectPhaseId: null,
      });
      setContact(value.contactId);
    }
  };
  //#endregion

  const getProjectPhases: (projectId: ProjectId) => void = async (
    projectId: ProjectId
  ) => {
    const { data } = await apiGetProjectPhases(projectId);
    if (data) {
      setProjectPhases(
        data.filter((phase) => phase.isActive && !phase.isArchived)
      );
    } else {
      setProjectPhases([]);
    }
  };

  //#region ------------------------------ Effects
  useEffect(() => {
    const fetchProjectPhases = async () => {
      // get projectPhases
      await getProjectPhases(activeProject);
    };
    if (activeProject) {
      fetchProjectPhases();
      dispatch(fetchInternalProjectContacts(activeProject));
    }
  }, [activeProject, dispatch, form]);

  useEffect(() => {
    dispatch(syncProjects());
  }, [dispatch]);

  useEffect(() => {
    if (contact && userMeContactId && projectContactIds) {
      if (contact !== userMeContactId) {
        if (!isInternalFetching && !projectContactIds.includes(contact)) {
          if (projectContactIds.includes(userMeContactId)) {
            form.setFieldsValue({ contactId: userMeContactId });
            setContact(userMeContactId);
          } else {
            form.setFieldsValue({ contactId: null });
            setContact(null);
          }
        }
      }
    }
  }, [projectContactIds, contact, form, userMeContactId, isInternalFetching]);

  //#endregion

  return (
    <Form
      className={classNames(classes.root, className)}
      form={form}
      onValuesChange={(
        changedValues: TimeRecordFormModel,
        allValues: TimeRecordFormModel
      ) => {
        if (changedValues.projectId) {
          dispatch(fetchInternalProjectContacts(changedValues.projectId));
          setCurrentProject(changedValues.projectId);
          getProjectPhases(changedValues.projectId);
          if (allValues.contactId) {
            if (contact === userMeContactId) {
              if (!myProjectIds.includes(changedValues.projectId)) {
                form.setFieldsValue({
                  contactId: null,
                });
                setContact(null);
              }
            }
          } else if (myProjectIds.includes(changedValues.projectId)) {
            form.setFieldsValue({
              contactId: userMeContactId,
            });
            setContact(userMeContactId);
          }
        }
        if (changedValues.contactId) {
          setContact(changedValues.contactId);
        }
        setDescriptionIsRequired(allValues.isSeparateService);
        if (
          allValues.title &&
          allValues.projectId &&
          form.getFieldValue('contactId')
        ) {
          if (allValues.isSeparateService && !allValues.description)
            setSaveButtonEnabled(false);
          else {
            setSaveButtonEnabled(true);
          }
        } else {
          setSaveButtonEnabled(false);
        }
        form.validateFields(['description']);
      }}
      onFinish={handleFinish}
      layout="vertical"
    >
      <div className={classes.scrollable}>
        <Flex.Row columnGap={theme.spacing.regular}>
          <Flex.Item flex={1}>
            <Form.Item
              initialValue={currentProjectId}
              name="projectId"
              label={t('timeRecords:form.labels.projectId')}
              rules={[
                {
                  required: true,
                  message: t('timeRecords:form.validation.missingProjectId'),
                },
              ]}
            >
              <ProjectPickerPrioCL
                disabled={disableForm}
                contextType={contextType}
                size="default"
                officeRoles={[
                  'officeAdmin',
                  'officeAssistance',
                  'officeController',
                ]}
              />
            </Form.Item>
          </Flex.Item>
          <Flex.Item flex={1}>
            <Form.Item
              initialValue={contact}
              name="contactId"
              label={t('timeRecords:form.labels.contactId')}
              rules={[
                {
                  required: true,
                  message: t('timeRecords:form.validation.missingContactId'),
                },
              ]}
            >
              <ContactPickerPrioCL
                disabled={!canSelectContact || disableForm || !currentProjectId}
                onlyInternalProject
                size="default"
                projectId={currentProjectId}
                loading={isFetchingMe && !!userMeContactId}
                hideShowMoreButton
              />
            </Form.Item>
          </Flex.Item>
        </Flex.Row>
        <Divider className={classes.divider} />
        <Flex.Column>
          <Form.Item name="dayRecordEntries" style={{ marginBottom: '0px' }}>
            <TimeRecordEntryManagement
              form={form}
              timeRecordForm={timeRecordForm}
              initialTimeRecordEntries={inintialTimeRecordEntries}
              isFormDisabled={disableForm}
              resetTimerecordformRef={resetTimerecordformRef}
            />
          </Form.Item>
          <Divider className={classes.divider2} />
        </Flex.Column>
        <Flex.Row columnGap={theme.spacing.regular}>
          <Flex.Item flex={2}>
            <Form.Item
              name="title"
              colon={colon}
              label={t('timeRecords:form.labels.title')}
              rules={[
                {
                  required: true,
                  message: t('timeRecords:form.validation.missingTitle'),
                },
              ]}
            >
              <Input disabled={disableForm} full />
            </Form.Item>
          </Flex.Item>
          <Flex.Item flex={1}>
            <Form.Item
              name="kilometerDistance"
              colon={colon}
              label={t('timeRecords:form.labels.kilometerDistance')}
              rules={[
                {
                  message: t(
                    'timeRecords:form.validation.invalidKilometerDistance'
                  ),
                  pattern: new RegExp('[0-9]+'),
                },
                () => ({
                  async validator(rule, value) {
                    if (value < 0) {
                      return Promise.reject(
                        t(
                          'timeRecords:form.validation.invalidKilometerDistance'
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                className={classes.fullWidth}
                min={0}
                disabled={disableForm}
                defaultValue={0}
                suffixIcon={['fal', 'road']}
              />
            </Form.Item>
          </Flex.Item>
        </Flex.Row>

        <Flex.Row childrenGap={theme.spacing.regular}>
          <Flex.Item flex={1}>
            <Form.Item
              name="isSeparateService"
              valuePropName="checked"
              label={t('timeRecords:form.labels.isSeparateService')}
            >
              <Checkbox />
            </Form.Item>
          </Flex.Item>
          <Flex.Item flex={1}>
            {projectPhases.length > 0 && (
              <Form.Item
                name="projectPhaseId"
                label={t('timeRecords:form.labels.projectPhase')}
              >
                <Select<string>
                  showSearch
                  value={projectPhases[0].projectPhaseId}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={projectPhases
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((phase) => ({
                      key: phase.projectPhaseId,
                      value: phase.projectPhaseId,
                      label: phase.name,
                    }))}
                />
              </Form.Item>
            )}
          </Flex.Item>
        </Flex.Row>

        <Form.Item
          name="description"
          label={t('timeRecords:form.labels.description')}
          rules={[
            {
              required: descriptionIsRequired,
              message: t('timeRecords:form.validation.missingDescription'),
            },
          ]}
          colon={colon}
          className={classes.noMargin}
        >
          <TextArea
            disabled={disableForm}
            autoSize={{ minRows: 3, maxRows: 8 }}
          />
        </Form.Item>
      </div>
      <Flex.Row justifyContent="end" className={classes.actionButtonsRow}>
        <Form.Item className={classes.submitButtonFormItem}>
          {onCancel && (
            <Button
              type="default"
              onClick={cancel}
              style={{ marginRight: '8px' }}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            htmlType="submit"
            disabled={disableActionButton || !saveButtonEnabled}
          >
            {actionLabel}
          </Button>
        </Form.Item>
      </Flex.Row>
    </Form>
  );
};

export default CreateTimeRecordForm;
